import axios from "axios";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type { components } from "@/types/api.generated";
import type {
  ICreateIdentityProvider,
  ISsoLinkedWorkspace,
  IWorkspaceDomain,
  IWorkspaceIdentityProvider,
} from "@/types/workspace";

/**
 * The exact text `IdentityProviderSerializer.validate()` raises for NG-1702's
 * single-active-provider rejection (FR21) — see `cognota/sso/serializers.py`
 * — mirrored here rather than the message the concurrent-activation race
 * reshapes it to (`cognota/core/views.py`'s `_UNIQUE_CONSTRAINT_MESSAGES`),
 * because both are worded identically by design (same failure, two catch
 * sites). No error code exists on either response shape to match on instead.
 */
const SSO_SINGLE_ACTIVE_PROVIDER_REJECTION =
  "Another identity provider is already active for this workspace. Disable it before activating another.";

/**
 * Whether a thrown error is specifically NG-1702's single-active-provider
 * rejection — never a generic "was this a 400" check, which would also match
 * every OTHER validation failure these endpoints can return (an incomplete
 * config, an invalid URL, an unrelated unique-constraint message), each of
 * which is meant to keep showing the existing generic create/update toast.
 *
 * The rejection can reach the client in EITHER of two 400 shapes depending on
 * which check caught it: the normal path raises a bare
 * `serializers.ValidationError` from the serializer's `validate()`, which DRF
 * places under `non_field_errors`; the rarer concurrent-activation race trips
 * the DB's partial-unique constraint instead, caught by `ExceptionHandlerMixin`
 * as an `IntegrityError` and reshaped to `{"error": message}`. Both are
 * checked here rather than assuming only the common path fires.
 */
export function isSsoSingleActiveProviderRejection(err: unknown): boolean {
  if (!axios.isAxiosError(err) || err.response?.status !== 400) return false;
  const data = err.response.data as { error?: string; non_field_errors?: string[] } | undefined;
  const message = (data?.non_field_errors?.[0] ?? data?.error ?? "").trim();
  return message === SSO_SINGLE_ACTIVE_PROVIDER_REJECTION;
}

/**
 * The server's reason for a failed domain-ownership check (NG-3229) — the 400
 * `{ error }` body `DomainVerifyEndpoint` returns when the DNS TXT record was
 * not found. `null` for anything else (a 403, a 5xx, a network failure), so
 * those keep the generic failure copy instead of an empty message.
 */
export function ssoDomainVerifyFailureMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 400) return null;
  const data = err.response.data as { error?: string } | undefined;
  return data?.error?.trim() || null;
}

/**
 * The role and teams SSO gives a person it signs up just in time (NG-3559). `role`
 * null means the workspace's Default role.
 */
export type SsoSignUpDefaults = components["schemas"]["SsoSignUpDefaults"];

/** What a metadata fill reads from (NG-3888): SAML text or URL, or an OIDC issuer. */
export type SsoMetadataFillRequest = components["schemas"]["SsoMetadataFillRequestRequest"];
/** The fields a metadata fill found, by protocol (NG-3888). Nothing is saved. */
export type SsoMetadataFill = components["schemas"]["SsoMetadataFill"];
export type SamlMetadataFill = components["schemas"]["SamlMetadataFill"];
export type OidcMetadataFill = components["schemas"]["OidcMetadataFill"];

/** Why a metadata fill failed: the server's code, and the status the IdP answered with. */
export interface SsoMetadataFillFailure {
  code: string;
  httpStatus: number | null;
}

/**
 * The reason a metadata fill was refused (NG-3888): the 400 `{ error, code,
 * http_status }` body `SsoMetadataFillEndpoint` answers when the metadata could
 * not be read, or `sso_metadata_throttled` for the endpoint's 10-a-minute
 * ceiling (a 429). `null` for anything else (a field error, a 403, a 5xx, a
 * network failure), which the panel words generically.
 */
export function ssoMetadataFillFailure(err: unknown): SsoMetadataFillFailure | null {
  if (!axios.isAxiosError(err)) return null;
  if (err.response?.status === 429) return { code: "sso_metadata_throttled", httpStatus: null };
  if (err.response?.status !== 400) return null;
  const data = err.response.data as { code?: unknown; http_status?: unknown } | undefined;
  if (typeof data?.code !== "string" || !data.code) return null;
  return {
    code: data.code,
    httpStatus: typeof data.http_status === "number" ? data.http_status : null,
  };
}

export class WorkspaceSsoService extends ApiService {
  /**
   * The workspace's SSO sign-up role and teams, as a sign-up would get them now.
   * The endpoint answers 404 while `sso-shared-identity-providers` is off.
   */
  async getSignUpDefaults(slug: string, config?: ApiRequestConfig): Promise<SsoSignUpDefaults> {
    return this.get(`/api/workspaces/${slug}/sso/sign-up-defaults/`, config);
  }

  async saveSignUpDefaults(slug: string, data: SsoSignUpDefaults): Promise<SsoSignUpDefaults> {
    return this.put(`/api/workspaces/${slug}/sso/sign-up-defaults/`, data);
  }

  async getDomains(slug: string, config?: ApiRequestConfig): Promise<IWorkspaceDomain[]> {
    return this.get(`/api/workspaces/${slug}/sso/domains/`, config);
  }

  async createDomain(slug: string, data: { domain: string }): Promise<IWorkspaceDomain> {
    return this.post(`/api/workspaces/${slug}/sso/domains/`, data);
  }

  async updateDomain(
    slug: string,
    domainId: string,
    data: { is_sso_enabled: boolean },
  ): Promise<IWorkspaceDomain> {
    return this.patch(`/api/workspaces/${slug}/sso/domains/${domainId}/`, data);
  }

  async deleteDomain(slug: string, domainId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/sso/domains/${domainId}/`);
  }

  /**
   * Trigger the DNS TXT ownership check for a claimed domain (NG-1705,
   * NG-3229). 200 returns the domain with `verified_at` set; a missing TXT
   * record is a 400 `{ error }` the caller surfaces to the admin.
   */
  async verifyDomain(slug: string, domainId: string): Promise<IWorkspaceDomain> {
    return this.post(`/api/workspaces/${slug}/sso/domains/${domainId}/verify/`);
  }

  async getProviders(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<IWorkspaceIdentityProvider[]> {
    return this.get(`/api/workspaces/${slug}/sso/providers/`, config);
  }

  async createProvider(
    slug: string,
    data: ICreateIdentityProvider,
  ): Promise<IWorkspaceIdentityProvider> {
    return this.post(`/api/workspaces/${slug}/sso/providers/`, data);
  }

  async updateProvider(
    slug: string,
    providerId: string,
    data: Partial<ICreateIdentityProvider>,
  ): Promise<IWorkspaceIdentityProvider> {
    return this.patch(`/api/workspaces/${slug}/sso/providers/${providerId}/`, data);
  }

  async deleteProvider(slug: string, providerId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/sso/providers/${providerId}/`);
  }

  /**
   * Read a connection's fields from its identity provider's metadata (NG-3888).
   * Saves nothing. The endpoint answers 404 while `sso-connection-metadata-fill` is off.
   */
  async fillFromMetadata(slug: string, data: SsoMetadataFillRequest): Promise<SsoMetadataFill> {
    return this.post(`/api/workspaces/${slug}/sso/providers/metadata/`, data);
  }

  /**
   * The workspaces this person could link the provider to (NG-3555): ones they
   * administer that don't use it and can take a link. The server builds it.
   */
  async getLinkTargets(
    slug: string,
    providerId: string,
    config?: ApiRequestConfig,
  ): Promise<ISsoLinkedWorkspace[]> {
    return this.get(`/api/workspaces/${slug}/sso/providers/${providerId}/link-targets/`, config);
  }

  /** Link the provider to `workspace` too; answers with the provider as this workspace reads it. */
  async linkProvider(
    slug: string,
    providerId: string,
    workspace: string,
  ): Promise<IWorkspaceIdentityProvider> {
    return this.post(`/api/workspaces/${slug}/sso/providers/${providerId}/links/`, { workspace });
  }

  /** Stop the URL's workspace using a provider set up in another one. */
  async unlinkProvider(slug: string, providerId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/sso/providers/${providerId}/links/`);
  }
}
